import { API_PREFIX, HEADERS, type ErrorCode } from "@l2a/shared";

/**
 * Typed fetch wrapper for the Lean2Automate API.
 *
 * - Same-origin cookie session (`credentials: "include"`).
 * - Double-submit CSRF: every unsafe method sends `x-l2a-csrf` = the `l2a_csrf` cookie.
 * - JSON in / JSON out; 204 → `undefined`.
 * - Errors are thrown as `ApiError` (shape from @l2a/shared `apiErrorSchema`).
 * - 401 UNAUTHENTICATED triggers the registered auth handler (redirect to /login),
 *   unless `skipAuthRedirect` is set (used by /auth/me itself and auth forms).
 * - Unsafe retries: pass `idempotencyKey` (string, or `true` to generate one).
 *   Create the key ONCE per user intent (e.g. when a dialog opens) and reuse it
 *   for retries so the server can de-duplicate.
 */

export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[];
export type QueryParams = Record<string, QueryValue>;

export interface RequestOptions {
  query?: QueryParams;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  idempotencyKey?: string | true;
  /** Don't run the global 401 handler (auth pages, session probe). */
  skipAuthRedirect?: boolean;
}

export type ApiErrorCode = ErrorCode | "NETWORK_ERROR" | "ABORTED" | "UNKNOWN";

export interface ValidationIssue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(init: { status: number; code: ApiErrorCode; message: string; details?: unknown; requestId?: string }) {
    super(init.message);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.details = init.details;
    this.requestId = init.requestId;
  }

  get isValidation(): boolean {
    return this.code === "VALIDATION_FAILED";
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  /**
   * Field-level problems from a VALIDATION_FAILED response. Accepts the common
   * shapes: `[{path, message}]` (org validation), zod issues `[{path: [..], message}]`,
   * or `{ fieldErrors: { name: ["msg"] } }`.
   */
  get issues(): ValidationIssue[] {
    return extractIssues(this.details);
  }
}

function extractIssues(details: unknown): ValidationIssue[] {
  const out: ValidationIssue[] = [];
  const visit = (d: unknown) => {
    if (Array.isArray(d)) {
      for (const item of d) {
        if (item && typeof item === "object" && "message" in item) {
          const rawPath = (item as { path?: unknown }).path;
          const path = Array.isArray(rawPath) ? rawPath.map(String).join(".") : typeof rawPath === "string" ? rawPath : "";
          out.push({ path, message: String((item as { message: unknown }).message) });
        }
      }
    } else if (d && typeof d === "object") {
      const obj = d as Record<string, unknown>;
      if (Array.isArray(obj.issues)) visit(obj.issues);
      if (obj.fieldErrors && typeof obj.fieldErrors === "object") {
        for (const [path, msgs] of Object.entries(obj.fieldErrors as Record<string, unknown>)) {
          const first = Array.isArray(msgs) ? msgs[0] : msgs;
          if (first) out.push({ path, message: String(first) });
        }
      }
    }
  };
  visit(details);
  return out;
}

/** Human message for any thrown value. */
export function errorMessage(err: unknown, fallback = "Something went wrong. Please try again."): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

// ---------------------------------------------------------------------------
// Auth hook (registered by AuthProvider; avoids an import cycle)
// ---------------------------------------------------------------------------

type UnauthenticatedHandler = (err: ApiError) => void;
let onUnauthenticated: UnauthenticatedHandler | null = null;
export function setUnauthenticatedHandler(handler: UnauthenticatedHandler | null): void {
  onUnauthenticated = handler;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const CSRF_COOKIE = "l2a_csrf";

export function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const c = part.trim();
    if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length));
  }
  return undefined;
}

export function createIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

/** Absolute API path: "/clients" → "/api/v1/clients"; "/api/…" paths are kept (portal). */
export function apiUrl(path: string, query?: QueryParams): string {
  const base = path.startsWith("/api/") ? path : `${API_PREFIX}${path.startsWith("/") ? "" : "/"}${path}`;
  const qs = query ? toSearchParams(query).toString() : "";
  return qs ? `${base}?${qs}` : base;
}

export function toSearchParams(query: QueryParams): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      if (value.length) params.set(key, value.join(","));
    } else {
      params.set(key, String(value));
    }
  }
  return params;
}

const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function buildHeaders(method: string, opts: RequestOptions, hasJsonBody: boolean): Headers {
  const headers = new Headers(opts.headers);
  headers.set("accept", "application/json");
  if (hasJsonBody) headers.set("content-type", "application/json");
  if (UNSAFE.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers.set(HEADERS.csrf, csrf);
    if (opts.idempotencyKey) {
      headers.set(HEADERS.idempotencyKey, opts.idempotencyKey === true ? createIdempotencyKey() : opts.idempotencyKey);
    }
  }
  return headers;
}

async function toApiError(res: Response): Promise<ApiError> {
  const requestId = res.headers.get(HEADERS.requestId) ?? undefined;
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON error (proxy, gateway) */
  }
  const e = (body as { error?: { code?: string; message?: string; details?: unknown; requestId?: string } } | null)?.error;
  if (e && typeof e.code === "string") {
    return new ApiError({
      status: res.status,
      code: e.code as ApiErrorCode,
      message: e.message || res.statusText,
      details: e.details,
      requestId: e.requestId ?? requestId,
    });
  }
  const fallbackCode: ApiErrorCode =
    res.status === 401 ? "UNAUTHENTICATED" : res.status === 403 ? "FORBIDDEN" : res.status === 404 ? "NOT_FOUND" : res.status === 429 ? "RATE_LIMITED" : res.status >= 500 ? "INTERNAL" : "UNKNOWN";
  const message =
    res.status === 502 || res.status === 503 || res.status === 504
      ? "The server is not reachable right now. Please try again in a moment."
      : res.statusText || `Request failed (${res.status})`;
  return new ApiError({ status: res.status, code: fallbackCode, message, requestId });
}

async function parseBody<T>(res: Response): Promise<T> {
  if (res.status === 204 || res.status === 205) return undefined as T;
  const type = res.headers.get("content-type") ?? "";
  if (type.includes("application/json")) return (await res.json()) as T;
  const text = await res.text();
  return (text ? text : undefined) as T;
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const upper = method.toUpperCase();
  const hasJsonBody = opts.body !== undefined && !(opts.body instanceof FormData);
  let res: Response;
  try {
    res = await fetch(apiUrl(path, opts.query), {
      method: upper,
      credentials: "include",
      headers: buildHeaders(upper, opts, hasJsonBody),
      body: opts.body === undefined ? undefined : opts.body instanceof FormData ? opts.body : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiError({ status: 0, code: "ABORTED", message: "Request cancelled" });
    }
    throw new ApiError({ status: 0, code: "NETWORK_ERROR", message: "You appear to be offline. Check your connection and try again." });
  }
  if (!res.ok) {
    const error = await toApiError(res);
    if (error.status === 401 && !opts.skipAuthRedirect) onUnauthenticated?.(error);
    throw error;
  }
  return parseBody<T>(res);
}

export interface UploadOptions {
  /** Extra multipart text fields (sent before the file, as the API expects). */
  fields?: Record<string, string | undefined | null>;
  /** Multipart field name of the binary (default "file"). */
  fieldName?: string;
  onProgress?: (fraction: number, loaded: number, total: number) => void;
  signal?: AbortSignal;
  method?: "POST" | "PUT";
  idempotencyKey?: string | true;
}

/** Multipart upload with progress (XHR, since fetch has no upload progress). */
export function upload<T>(path: string, file: Blob & { name?: string }, opts: UploadOptions = {}): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const form = new FormData();
    for (const [k, v] of Object.entries(opts.fields ?? {})) if (v !== undefined && v !== null) form.append(k, v);
    form.append(opts.fieldName ?? "file", file, file.name ?? "file");

    const xhr = new XMLHttpRequest();
    xhr.open(opts.method ?? "POST", apiUrl(path));
    xhr.withCredentials = true;
    xhr.setRequestHeader("accept", "application/json");
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) xhr.setRequestHeader(HEADERS.csrf, csrf);
    if (opts.idempotencyKey) xhr.setRequestHeader(HEADERS.idempotencyKey, opts.idempotencyKey === true ? createIdempotencyKey() : opts.idempotencyKey);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total, e.loaded, e.total);
    };
    xhr.onload = () => {
      const requestId = xhr.getResponseHeader(HEADERS.requestId) ?? undefined;
      let body: unknown = undefined;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
      } catch {
        body = xhr.responseText;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        opts.onProgress?.(1, 1, 1);
        resolve(body as T);
        return;
      }
      const e = (body as { error?: { code?: string; message?: string; details?: unknown; requestId?: string } } | undefined)?.error;
      const error = new ApiError({
        status: xhr.status,
        code: (e?.code as ApiErrorCode) ?? (xhr.status === 413 ? "PAYLOAD_TOO_LARGE" : "UNKNOWN"),
        message: e?.message ?? (xhr.status === 413 ? "The file is too large." : `Upload failed (${xhr.status})`),
        details: e?.details,
        requestId: e?.requestId ?? requestId,
      });
      if (xhr.status === 401) onUnauthenticated?.(error);
      reject(error);
    };
    xhr.onerror = () => reject(new ApiError({ status: 0, code: "NETWORK_ERROR", message: "Upload failed: network error" }));
    xhr.onabort = () => reject(new ApiError({ status: 0, code: "ABORTED", message: "Upload cancelled" }));
    if (opts.signal) {
      if (opts.signal.aborted) {
        xhr.abort();
        return;
      }
      opts.signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(form);
  });
}

/** The API client. Paths are relative to /api/v1 unless they start with /api/. */
export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, "body">) => request<T>("GET", path, opts),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "body">) => request<T>("POST", path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "body">) => request<T>("PUT", path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "body">) => request<T>("PATCH", path, { ...opts, body }),
  delete: <T = void>(path: string, opts?: RequestOptions) => request<T>("DELETE", path, opts),
  upload,
  url: apiUrl,
};
